import type { AxiosError, AxiosInstance } from 'axios';

/**
 * Swallow the rejection from a background XHR that fails because the session
 * has gone (401 Unauthorized) or expired (419 Page Expired / CSRF token
 * mismatch).
 *
 * Our `internal-api` endpoints are session-guarded, and `bootstrap.ts` stamps
 * every axios request with `X-Requested-With: XMLHttpRequest`. That makes
 * Laravel's `auth` middleware treat the request as `expectsJson()` and answer
 * a lapsed session with a JSON 401 rather than the 302-to-login a top-level
 * navigation would get. Nothing handles that 401, so the rejected promise
 * (e.g. from the address-search typeahead) reaches Sentry's global
 * `onunhandledrejection` handler as an unhandled rejection (SPD-383).
 *
 * Returning a promise that never settles leaves the caller pending without
 * rejecting, so the auth failure no longer surfaces as unhandled noise. Any
 * other status re-rejects unchanged for normal per-call handling.
 *
 * Note: this deliberately does not redirect. A swallowed call simply never
 * resolves, so a control awaiting it (the typeahead) stays in its loading
 * state until the user reloads or navigates.
 */
export function suppressAuthErrorRejections(axios: AxiosInstance): void {
  axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;

      if (status === 401 || status === 419) {
        return new Promise<never>(() => {});
      }

      return Promise.reject(error);
    },
  );
}
